import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Popup, Input } from 'semantic-ui-react';
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';
import { getDateRangeForPreset } from './dateRangePresets';
import './DateRangeFilter.scss';

const PRESET_KEYS = {
  THIS_YEAR: 'datePresets.thisYear',
  LAST_YEAR: 'datePresets.lastYear',
  LAST_3_MONTHS: 'datePresets.last3Months',
  LAST_6_MONTHS: 'datePresets.last6Months',
  FIRST_SEMESTER: 'datePresets.firstSemester',
  SECOND_SEMESTER: 'datePresets.secondSemester',
};

const isYearValid = (value) => {
  const m = value && value.match(/^(\d{4})-\d{2}-\d{2}$/);
  if (!m) return false;
  const y = parseInt(m[1], 10);
  return y >= 1990 && y <= 2050;
};

const formatDateForInput = (date) => {
  if (!date) return '';
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  if (typeof date === 'string') {
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }
  if (date instanceof Date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
};

export const DateRangeField = ({
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  inputClassName,
  min,
  id,
  disabled,
  fieldClassName = '',
}) => {
  const inputRef = useRef(null);

  const handleOpenPicker = () => {
    if (disabled) return;
    const input = inputRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // ignorar si el navegador bloquea showPicker
      }
    }
    input.focus();
    input.click();
  };

  return (
    <div className={`date-range-filter__field${fieldClassName ? ` ${fieldClassName}` : ''}`}>
      <input
        ref={inputRef}
        id={id}
        type="date"
        aria-label={label}
        value={value}
        min={min || undefined}
        disabled={disabled}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={inputClassName}
      />
      <button
        type="button"
        className="date-range-filter__picker-btn"
        tabIndex={-1}
        aria-label={`Abrir calendario (${label})`}
        disabled={disabled}
        onClick={handleOpenPicker}
      >
        <FeatherIcon icon="calendar" size={14} />
      </button>
    </div>
  );
};

const DateRangeFilter = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onPresetSelect,
  className = '',
  showActiveIndicator = true,
  startPlaceholder,
  endPlaceholder,
  size = 'small',
  presetNs = 'documents',
  minDate,
}) => {
  const { t, i18n } = useTranslation(presetNs);
  const resolvedStartPlaceholder = startPlaceholder ?? t('datePresets.from');
  const resolvedEndPlaceholder = endPlaceholder ?? t('datePresets.to');

  const valueStart = formatDateForInput(startDate) ?? '';
  const valueEnd = formatDateForInput(endDate) ?? '';
  const hasActiveDates = showActiveIndicator && (valueStart || valueEnd);

  const [draftStart, setDraftStart] = useState(valueStart);
  const [draftEnd, setDraftEnd] = useState(valueEnd);

  useEffect(() => {
    setDraftStart(valueStart);
  }, [valueStart]);

  useEffect(() => {
    setDraftEnd(valueEnd);
  }, [valueEnd]);

  const clampToMin = (raw) => {
    if (!raw || !minDate || !isYearValid(raw)) return raw;
    return raw < minDate ? minDate : raw;
  };

  const handleStartChange = (e) => {
    const raw = clampToMin(e.target.value || '');
    setDraftStart(raw);
    if (!raw) {
      onStartDateChange('');
      return;
    }
    if (isYearValid(raw)) onStartDateChange(raw);
  };

  const handleEndChange = (e) => {
    const raw = clampToMin(e.target.value || '');
    setDraftEnd(raw);
    if (!raw) {
      onEndDateChange('');
      return;
    }
    if (isYearValid(raw)) onEndDateChange(raw);
  };

  const handleStartBlur = () => {
    if (draftStart && !isYearValid(draftStart)) {
      setDraftStart('');
      onStartDateChange('');
    }
  };

  const handleEndBlur = () => {
    if (draftEnd && !isYearValid(draftEnd)) {
      setDraftEnd('');
      onEndDateChange('');
    }
  };

  const handlePresetSelect = (preset) => {
    if (preset === 'RESET') {
      onStartDateChange('');
      onEndDateChange('');
      if (onPresetSelect) onPresetSelect(preset, '', '');
      return;
    }

    const { startDate: presetStart, endDate: presetEnd } = getDateRangeForPreset(preset);
    if (!presetStart || !presetEnd) return;
    if (minDate && presetEnd < minDate) return;
    const nextStart = minDate && presetStart < minDate ? minDate : presetStart;
    if (onPresetSelect) {
      onPresetSelect(preset, nextStart, presetEnd);
    } else {
      onStartDateChange(nextStart);
      onEndDateChange(presetEnd);
    }
  };

  const presetOptions = useMemo(
    () => (
      <div className="tw-flex tw-flex-col tw-gap-1" key={i18n.language}>
        {Object.keys(PRESET_KEYS).map((preset) => (
          <div
            key={preset}
            onClick={() => handlePresetSelect(preset)}
            className="tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md reconcile-filter-option tw-text-gray-700 hover:tw-bg-gray-50"
          >
            {t(PRESET_KEYS[preset])}
          </div>
        ))}
        <div
          onClick={() => handlePresetSelect('RESET')}
          className="tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md reconcile-filter-option tw-text-gray-700 hover:tw-bg-gray-50"
        >
          {t('datePresets.reset')}
        </div>
      </div>
    ),
    [t, i18n.language, minDate],
  );

  const inputClassName = `date-range-filter__input${size === 'small' ? ' date-range-filter__input--small' : ''}`;

  return (
    <div className={`date-range-filter ${className}`}>
      <DateRangeField
        label={startPlaceholder}
        value={draftStart}
        min={minDate}
        onChange={handleStartChange}
        onBlur={handleStartBlur}
        inputClassName={inputClassName}
      />
      <FeatherIcon icon="arrow-right" size={12} className="tw-text-gray-400 tw-flex-shrink-0" />
      <DateRangeField
        label={endPlaceholder}
        value={draftEnd}
        min={minDate}
        onChange={handleEndChange}
        onBlur={handleEndBlur}
        inputClassName={inputClassName}
      />
      <Popup
        trigger={
          <div className="tw-cursor-pointer tw-p-1 date-range-filter-calendar-icon tw-hover:bg-gray-100 tw-flex-shrink-0">
            <FeatherIcon
              icon="calendar"
              size={12}
              className={hasActiveDates ? 'tw-text-[#488E9D]' : 'tw-text-gray-600'}
            />
          </div>
        }
        content={presetOptions}
        position="bottom right"
        on="hover"
        hoverable
        className="reconcile-filter-popup-wrapper"
      />
    </div>
  );
};

export default DateRangeFilter;
